import React from "react";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useGetFormDetailsQuery } from "../../store/api/apiSlice";
import { SurveyHeader } from "../../components/survey/SurveyHeader";
import RetryRewardButton from "../../components/survey/RetryRewardButton";
import { getSurveyTrackingToken } from "../../utils/surveyTracking";
import {
  persistSurveyPayoutState,
} from "../../utils/surveyPayout";

const PANEL_SURVEY_MESSAGES = {
  submitted: {
    title: "This survey has already been submitted",
    description:
      "You have already completed this survey. Thank you for your participation.",
  },
  unavailable: {
    title: "This survey is unavailable",
    description: "This survey is no longer available.",
  },
};

const PanelErrorPage = () => {
  const { formId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const enquiryType = searchParams.get("type");
  const resolvedFormId = formId || searchParams.get("formId");
  const { data: formData } = useGetFormDetailsQuery(resolvedFormId, {
    skip: !resolvedFormId,
  });

  const isAdminThankYou = location.pathname.startsWith(
    "/admin/question/thank-you",
  );
  const reason =
    location.state?.reason === "submitted" ? "submitted" : "unavailable";
  const copy = PANEL_SURVEY_MESSAGES[reason];
  const panelToken = getSurveyTrackingToken(resolvedFormId, searchParams);
  const [payoutState, setPayoutState] = React.useState(
    () => location.state?.payout || null,
  );
  const canRetryPayout =
    Boolean(location.state?.canRetryPayout) ||
    Boolean(payoutState?.canRetry) ||
    false;

  return (
    <div className="box-border flex h-dvh flex-col p-2 sm:p-3">
      <div className="mx-auto flex min-h-0 w-full flex-1 flex-col">
        {enquiryType !== "enquiry" ? (
          <SurveyHeader
            organization={formData?.data?.organization}
            form={formData?.data}
            onCancel={() =>
              navigate(isAdminThankYou ? "/admin/dashboard" : "/")
            }
          />
        ) : (
          <div className="h-24 md:h-32" />
        )}
        <div className="min-h-0 w-full flex-1">
          <div className="flex min-h-0 flex-col items-center justify-center gap-6 overflow-x-hidden rounded-3xl border bg-gray-50 p-2 text-center max-md:flex-1 max-md:min-h-0 max-md:overflow-y-auto md:h-[calc(100%-35px)] md:overflow-hidden md:p-5 lg:h-[calc(100%-60px)]">
            <div className="flex flex-col items-center gap-5 text-center md:gap-[clamp(1rem,2.25dvh_+_0.5rem,2rem)]">
              <h1 className="mx-auto m-0 max-w-3xl whitespace-normal break-words text-3xl font-semibold leading-snug text-gray-900 md:leading-[1.08] md:text-[clamp(1.75rem,1.1rem_+_4.2dvh,4rem)]">
                {copy.title}
              </h1>
              <p className="m-0 max-w-md text-base leading-relaxed text-gray-900 md:text-[clamp(0.9375rem,0.72rem_+_2.25dvh,2rem)]">
                {location.state?.message || copy.description}
              </p>
            </div>
            {canRetryPayout && panelToken && !payoutState?.payoutSent ? (
              <RetryRewardButton
                token={panelToken}
                formId={resolvedFormId}
                onPayoutUpdate={setPayoutState}
                onSuccess={(payout) => {
                  if (payout?.payoutSent) {
                    setPayoutState(payout);
                    persistSurveyPayoutState(resolvedFormId, payout);
                  }
                }}
              />
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PanelErrorPage;
